/* ============================================================
   Vibe — card styling preset for the For You feed. Three modes
   ported verbatim from the Stats Inside design package:

     headline   Editorial · magazine  (cream/black, serif vibes)
     broadcast  Scoreboard · neon     (dark, accent-green, default)
     chaos      Sticker · meme-core   (yellow, Impact type, outlines)

   CSS rules live in `src/styles/foryou.css` under
   `[data-vibe="..."]` selectors. This module just handles the
   state: persist to localStorage, sync across components.
   ============================================================ */

import { useSyncExternalStore } from 'react';

export const VIBES = ['headline', 'broadcast', 'chaos'] as const;
export type Vibe = (typeof VIBES)[number];

export const VIBE_META: Record<Vibe, { name: string; sub: string }> = {
  headline: { name: 'Headline', sub: 'Editorial · magazine' },
  broadcast: { name: 'Broadcast', sub: 'Scoreboard · neon' },
  chaos: { name: 'Chaos', sub: 'Sticker · meme-core' },
};

const STORAGE_KEY = 'fs.vibe';
const DEFAULT: Vibe = 'broadcast';
const EVENT = 'fs:vibe-change';

function read(): Vibe {
  if (typeof window === 'undefined') return DEFAULT;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return (VIBES as readonly string[]).includes(raw ?? '')
    ? (raw as Vibe)
    : DEFAULT;
}

export function setVibe(v: Vibe) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, v);
  // Notify same-tab listeners. `storage` events only fire cross-tab.
  window.dispatchEvent(new CustomEvent(EVENT));
}

function subscribe(cb: () => void) {
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', cb);
  };
}

export function useVibe(): Vibe {
  return useSyncExternalStore(subscribe, read, () => DEFAULT);
}
